import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Camera, X, ExternalLink, Newspaper, ChevronLeft, ChevronRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { getGallery, getMedia } from "../lib/api";

export default function Gallery() {
  const [events, setEvents] = useState([]);
  const [media, setMedia] = useState([]);
  const [active, setActive] = useState(null); // active event for modal
  const [photoIdx, setPhotoIdx] = useState(0);

  useEffect(() => {
    getGallery().then(setEvents).catch(() => {});
    getMedia().then(setMedia).catch(() => {});
  }, []);

  const open = (event) => {
    setActive(event);
    setPhotoIdx(0);
    document.body.style.overflow = "hidden";
  };
  const close = () => {
    setActive(null);
    document.body.style.overflow = "";
  };
  const prev = () => active && setPhotoIdx((i) => (i - 1 + active.photos.length) % active.photos.length);
  const next = () => active && setPhotoIdx((i) => (i + 1) % active.photos.length);

  return (
    <div data-testid="gallery-page">
      <PageHeader
        eyebrow="Moments"
        title="Glimpses of college life."
        subtitle="Browse photos from our events, celebrations, and campus moments. Click on any event to see all photos with captions."
      />

      {/* Events */}
      <section className="py-16 lg:py-24 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 max-w-2xl">
            <div className="eyebrow mb-2">Events & Albums</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary leading-tight">
              Click an album to view all photos.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7" data-testid="events-grid">
            {events.map((e, i) => (
              <motion.button
                key={e.id}
                onClick={() => open(e)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="text-left bg-white rounded-3xl overflow-hidden border border-brand-primary/10 lift-card group"
                data-testid={`event-card-${i}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={e.cover_image}
                    alt={e.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/85 via-brand-primary/20 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand-secondary text-brand-primary text-[10px] font-semibold tracking-[0.2em] uppercase rounded-full mb-2">
                      <Camera className="w-3 h-3" />
                      {e.photos.length} photos
                    </div>
                    <h3 className="font-serif text-2xl leading-tight">{e.title}</h3>
                  </div>
                </div>
                <div className="p-5">
                  {e.date && (
                    <div className="flex items-center gap-2 text-xs text-brand-muted mb-2">
                      <Calendar className="w-3.5 h-3.5" /> {e.date}
                    </div>
                  )}
                  <p className="text-sm text-brand-ink/75 leading-relaxed line-clamp-2">
                    {e.description}
                  </p>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Media Coverage */}
      {media.length > 0 && (
        <section className="py-16 lg:py-24 bg-brand-accent/40" data-testid="media-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 max-w-2xl">
              <div className="eyebrow mb-2 flex items-center gap-2">
                <Newspaper className="w-3.5 h-3.5" /> In the Media
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary leading-tight">
                Newspaper coverage & external articles.
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7" data-testid="media-grid">
              {media.map((m, i) => (
                <motion.a
                  key={m.id}
                  href={m.external_url || m.image_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white rounded-3xl overflow-hidden border border-brand-primary/10 lift-card group"
                  data-testid={`media-card-${i}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-brand-accent/40">
                    <img
                      src={m.image_url}
                      alt={m.title}
                      className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h3 className="font-serif text-lg text-brand-primary leading-snug">
                        {m.title}
                      </h3>
                      <ExternalLink className="w-4 h-4 text-brand-secondary flex-shrink-0" />
                    </div>
                    <p className="text-sm text-brand-ink/75 leading-relaxed mb-3 line-clamp-3">
                      {m.caption}
                    </p>
                    <div className="text-xs text-brand-muted flex items-center gap-2 flex-wrap">
                      {m.source && <span>{m.source}</span>}
                      {m.source && m.date && <span>·</span>}
                      {m.date && <span>{m.date}</span>}
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-brand-primary/95 backdrop-blur-md flex flex-col"
            onClick={close}
            data-testid="event-lightbox"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 text-white" onClick={(e) => e.stopPropagation()}>
              <div>
                <div className="font-serif text-xl sm:text-2xl">{active.title}</div>
                <div className="text-xs text-white/70 tracking-wide">{active.date}</div>
              </div>
              <button
                onClick={close}
                aria-label="Close"
                data-testid="lightbox-close"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Image */}
            <div
              className="flex-1 min-h-0 overflow-hidden flex items-center justify-center p-4 sm:p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {active.photos.length > 1 && (
                <button
                  onClick={prev}
                  aria-label="Previous"
                  data-testid="lightbox-prev"
                  className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/15 hover:bg-brand-secondary hover:text-brand-primary text-white flex items-center justify-center transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              )}

              <motion.img
                key={photoIdx}
                src={active.photos[photoIdx]?.url}
                alt={active.photos[photoIdx]?.caption || active.title}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="max-h-[calc(100vh-230px)] sm:max-h-[calc(100vh-240px)] max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-8rem)] object-contain rounded-2xl shadow-2xl"
              />

              {active.photos.length > 1 && (
                <button
                  onClick={next}
                  aria-label="Next"
                  data-testid="lightbox-next"
                  className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/15 hover:bg-brand-secondary hover:text-brand-primary text-white flex items-center justify-center transition-colors"
                >
                  <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              )}
            </div>

            {/* Caption */}
            <div className="px-4 sm:px-8 pb-6 sm:pb-8 text-white" onClick={(e) => e.stopPropagation()}>
              <div className="max-w-3xl mx-auto bg-white/10 backdrop-blur-md rounded-2xl px-5 py-4 border border-white/20">
                <div className="flex items-center justify-between gap-4 mb-1">
                  <span className="text-xs uppercase tracking-[0.2em] text-brand-secondary font-semibold">
                    Photo {photoIdx + 1} of {active.photos.length}
                  </span>
                  <span className="text-xs text-white/60">Click outside to close</span>
                </div>
                <p className="text-sm sm:text-base leading-relaxed" data-testid="lightbox-caption">
                  {active.photos[photoIdx]?.caption || active.title}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
